<template>
  <b-card no-body>
    <b-card-body>
      <!-- App Header -->
      <div class="d-flex align-items-start mb-2">
        <b-avatar
          rounded
          size="70"
          variant="light"
        >
          <b-img
            :src="app.image"
            width="70"
            height="auto"
          />
        </b-avatar>
        <div class="ml-1 flex-grow-1">
          <h5 class="mb-25">
            {{ app.name }}
          </h5>
          <small class="text-muted">{{ app.short_description }}</small>
          <div class="mt-50">
            <!-- Pricing Badge -->
            <b-badge
              :variant="app.is_free ? 'light-primary' : 'light-info'"
              pill
            >
              <material-icon
                  :icon="app.is_free ? 'money_off' : 'attach_money'"
                  size="14"
                  font-weight="bold"
                  class="mr-25"
              />
              {{ app.is_free ? $t('modules.apps._messages.free') : $t('modules.apps._messages.paid') }}
            </b-badge>

            <b-badge
              class="ml-25"
              variant="light-secondary"
              pill
            >
              {{ app.category }}
            </b-badge>
          </div>
        </div>
      </div>

      <!--      <hr>-->

      <!--      &lt;!&ndash; Developer Info &ndash;&gt;-->
      <!--      <div class="mb-1">-->
      <!--        <small class="text-muted d-block mb-25">-->
      <!--          <feather-icon-->
      <!--            icon="UserIcon"-->
      <!--            size="14"-->
      <!--            class="mr-25"-->
      <!--          />-->
      <!--          {{ $t('modules.apps._messages.developer') }}: {{ app.developer.name }}-->
      <!--        </small>-->
      <!--        <small class="text-muted d-block">-->
      <!--          <feather-icon-->
      <!--            icon="MailIcon"-->
      <!--            size="14"-->
      <!--            class="mr-25"-->
      <!--          />-->
      <!--          {{ app.developer.email }}-->
      <!--        </small>-->
      <!--      </div>-->

      <!-- Subscription Info -->
      <div
        v-if="app.subscription"
        class="subscription-info"
      >
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h6 class="mb-0">
            <feather-icon
              icon="CalendarIcon"
              size="16"
              class="mr-50"
            />
            {{ $t('modules.apps._messages.subscription') }}
          </h6>
          <b-badge
            :variant="getSubscriptionStatusVariant(app.subscription.status_class)"
            pill
          >
            {{ getSubscriptionStatusLabel(app.subscription.status) }}
          </b-badge>
        </div>

        <!-- Plan Name -->
        <div class="d-flex justify-content-between mb-50">
          <small class="text-muted">{{ $t('modules.apps._messages.plan') }}:</small>
          <small class="font-weight-bold">{{ app.subscription.plan.short_name }}</small>
        </div>

        <!--        &lt;!&ndash; Price &ndash;&gt;-->
        <!--        <div class="d-flex justify-content-between mb-50">-->
        <!--          <small class="text-muted">{{ $t('modules.apps._messages.price') }}:</small>-->
        <!--          <small class="font-weight-bold">{{ formatPrice(app.subscription.price) }}</small>-->
        <!--        </div>-->

        <!-- Start Date -->
        <div class="d-flex justify-content-between mb-50">
          <small class="text-muted">{{ $t('modules.apps._messages.start_date') }}:</small>
          <small>{{ formatDate(app.subscription.start_at) }}</small>
        </div>

        <!-- End Date -->
        <div class="d-flex justify-content-between mb-50">
          <small class="text-muted">{{ $t('modules.apps._messages.end_date') }}:</small>
          <small>{{ formatDate(app.subscription.end_at) }}</small>
        </div>

        <!-- Trial Badge -->
        <div
          v-if="app.subscription.is_trial"
          class="mb-50"
        >
          <b-badge
            variant="light-info"
            pill
          >
            <feather-icon
              icon="ClockIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('modules.apps._messages.trial') }}
          </b-badge>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-1 mt-1">
          <b-button
            v-if="app.subscription.is_renewable"
            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
            variant="outline-success"
            block
            @click="$emit('renew-subscription', app.subscription.plan.code)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('modules.apps._messages.renew') }}
          </b-button>
        </div>
      </div>

      <!-- No Subscription (Free App) -->
      <div
        v-else-if="app.is_free"
        class="mt-1"
      >
        <b-alert
          variant="success"
          show
        >
          <div class="alert-body">
            <feather-icon
              icon="CheckCircleIcon"
              size="16"
              class="mr-50"
            />
            <span>{{ $t('modules.apps._messages.free_app_active') }}</span>
          </div>
        </b-alert>
      </div>

      <!-- View Details Button -->
      <div class="mt-1">
        <b-button
          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
          variant="flat-primary"
          block
          :to="{ name: 'apps-app', params: { category: app.category, app: app.slug } }"
        >
          <feather-icon
            icon="EyeIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('modules.apps._messages.view_details') }}
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BAvatar,
  BImg,
  BBadge,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useUtils as useI18nUtils } from '@core/libs/i18n'

export default {
  components: {
    BCard,
    BCardBody,
    BAvatar,
    BImg,
    BBadge,
    BButton,
    BAlert,
  },
  emits: ['renew-subscription'],
  directives: {
    Ripple,
  },
  props: {
    app: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { t } = useI18nUtils()

    const formatDate = dateString => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    const formatPrice = price => {
      if (!price) return '-'
      return new Intl.NumberFormat('ar-SA', {
        style: 'currency',
        currency: 'SAR',
      }).format(price)
    }

    const getSubscriptionStatusVariant = statusClass => {
      const variantMap = {
        warning: 'light-warning',
        success: 'light-success',
        danger: 'light-danger',
        info: 'light-info',
      }
      return variantMap[statusClass] || 'light-secondary'
    }

    const getSubscriptionStatusLabel = status => {
      const statusMap = {
        ACTV: t('modules.apps._messages.status_active'),
        EXPR: t('modules.apps._messages.status_expired'),
        CANC: t('modules.apps._messages.status_cancelled'),
        PEND: t('modules.apps._messages.status_pending'),
      }
      return statusMap[status] || status
    }

    return {
      formatDate,
      formatPrice,
      getSubscriptionStatusVariant,
      getSubscriptionStatusLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.subscription-info {
  background-color: #f8f8f8;
  padding: 1rem;
  border-radius: 0.357rem;
  margin-top: 1rem;
}

.gap-1 {
  gap: 0.5rem;
}
</style>
